/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {UserBaseModel} from '../../api/ms-security/contracts/user-base.model';
import {UserService} from '../../shared/services/user.service';
import {UserSummaryModel} from '../../api/ms-security/contracts/user-summary.model';
import {Permission} from '../../shared/utils/permission.enum';
import {UserManagementService} from '../../api/ms-security/user-management/user-management.service';
import {InputAutocompleteComponent} from '../../shared/input/input-autocomplete/input-autocomplete.component';
import {TypeaheadHelperService} from '../../shared/services/typeahead-helper.service';
import {ActivatedRoute} from '@angular/router';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {RolesManagementStateModel} from './users-table/roles-management-state.model';

@Component({
    selector: 'app-roles-management',
    templateUrl: './roles-management.component.html',
    styleUrls: ['./roles-management.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RolesManagementComponent extends StatefulComponent<RolesManagementStateModel> implements OnInit, OnDestroy {
    @ViewChild('findUser') findUser: InputAutocompleteComponent;
    public userValue: any;
    /** User to add event emitter **/
    public userToAdd = new Subject<UserSummaryModel>();
    /** Show or hide add user **/
    public showAddUser: boolean;
    private subscriptions = new Subscription();

    constructor(private loadingService: LoadingService,
                private userService: UserService,
                private rolesService: UserManagementService,
                private route: ActivatedRoute,
                public typeahead: TypeaheadHelperService) {
        super();
        this.loadingService.stop();
    }

    ngOnInit(): void {
        this.subscriptions.add(
                this.route.url.subscribe(() => {
                    this.storeState({context: this.route.snapshot.data['context']});
                }));

        this.showAddUser = this.userService.hasPermissions([
            Permission.PARTNER_ROLE_REVOKE,
            Permission.ADMIN_ROLE_REVOKE,
            Permission.ADMIN_ROLE_GRANT,
            Permission.PARTNER_ROLE_GRANT
        ]);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Find users with no roles autocomplete function
     * @param term The text passed in the input field
     **/
    searchUsersWithNoRoles = (term: string): Observable<UserBaseModel[]> => {
        const contextValue = this.state.context;

        if (term.length > 0) {
            return this.rolesService.getAutocompleteUserWithNoRolesList(term, contextValue);
        }
        return of([]);
    };

    /**
     * Adds the selected user with no roles to the table
     **/
    addUserToTable = (): void => {
        if (this.findUser.isValid) {
            const user: UserBaseModel = this.userValue;

            if (user) {
                const newTableUser: UserSummaryModel = {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username,
                    roles: [],
                    partners: [],
                    groups: []
                };
                this.userToAdd.next(newTableUser);
                this.findUser.deleteInputValue();
            }
        }
    };

    disableButton = (): boolean => {
        if (this.findUser && !this.findUser.isValid || this.userValue == null || this.userValue === '') {
            return true;
        }
    };

    updateUserInputValue = (event: any): void => {
        this.userValue = event;
    };
}


